import LocationPanels from "./LocationPanels";
import {useEffect, useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {getCallDetails, getGroupCalls} from '../api/calls';
import {getMarkerToDisplay, resolveFloorPlan} from '../util/locationFallback';
import Maps from './Maps';
import GoogleMapLoader from './map/GoogleMapLoader';
import RelativeFloorPlan from './map/RelativeFloorPlan';

// Resolve only the latest push supplied by the list API. Calls without a displayable
// location remain in the sidebar/count, but cannot contribute a map marker.
export function groupEntries(calls, alsEnabled = false) {
    return calls.map(call => {
        const payload = call.eml?.[0]?.payload?.[0];
        if (!payload) return null;
        // Match single-call confidence ordering without mutating React Query cached data.
        const rankedLocations = [...(payload.rankedLocations || [])].sort((a, b) => b.confidence - a.confidence);
        const latest = {...payload, rankedLocations};
        const marker = getMarkerToDisplay(latest, alsEnabled);
        if (!marker) return null;
        const plan = resolveFloorPlan([], latest);
        const name = plan.isSbmShipLocation ? 'SBM Ship' : plan.isEliOfficeLocation ? 'ELi Office'
            : plan.isTestLocation ? 'Myles Brand Hall' : plan.isMYLocation ? 'Manu Tests' : 'GPS';
        // Pixel coordinates belong to their floor plan; GPS points share the world map.
        // Floor-specific contexts also prevent unrelated floors being overlaid together.
        const context = name === 'GPS' || marker.type === 'gps' ? 'GPS' : `${name} — Floor ${marker.floor ?? 'unknown'}`;
        return {context, plan: context === 'GPS' ? {} : plan,
            marker: {...marker, id: call.callUuid, callUuid: call.callUuid, isLatest: true, timestampLabel: [call.sourceIdentifier1, call.phoneNumber, call.callUuid]
                .map(value => String(value ?? '').trim()).find(Boolean)}};
    }).filter(Boolean);
}

export default function GroupOverview({filters, selectedCall, onSelect}) {
    const {sourceIdentifier2, from, to, lat, lng} = filters;
    // Exclude sidebar pagination from this key: changing its page must not shrink or
    // reload the overview. Full history is fetched separately for the selected call only.
    const overview = useQuery({queryKey: ['group-calls', {sourceIdentifier2, from, to, lat, lng}],
        queryFn: getGroupCalls, refetchInterval: 30000});
    const detail = useQuery({queryKey: ['get-call-detail-' + selectedCall, {callUuid: selectedCall}],
        queryFn: getCallDetails, enabled: !!selectedCall, refetchInterval: 10000});
    const [context, setContext] = useState('');
    const entries = useMemo(() => groupEntries(overview.data || []), [overview.data]);
    const contexts = [...new Set(entries.map(entry => entry.context))];
    const selectedContext = entries.find(entry => entry.marker.callUuid === selectedCall)?.context;
    // Sidebar selection can target a call on another plan or floor. Follow it, while
    // still allowing the user to browse other contexts without changing the selection.
    useEffect(() => {if (selectedContext) setContext(selectedContext);}, [selectedContext]);
    const activeContext = contexts.includes(context) ? context : contexts[0];
    // Copy and sort selected history payloads for the existing Maps detail controls.
    const history = useMemo(() => (detail.data?.[0]?.eml || []).map(push => ({...push,
        payload: push.payload?.map(payload => ({...payload,
            rankedLocations: [...(payload.rankedLocations || [])].sort((a, b) => b.confidence - a.confidence)}))})), [detail.data]);
    const renderMap = ({locationMarker, allLocationMarkers = [], showAllLocations, alsEnabled = false} = {}) => {
        const visible = groupEntries(overview.data || [], alsEnabled).filter(e => e.context === activeContext);
        let markers = visible.map(e => e.marker);
        if (selectedCall && markers.some(m => m.callUuid === selectedCall)) {
            const selectedMarkers = showAllLocations ? allLocationMarkers : locationMarker ? [locationMarker] : [];
            // Retain every call's latest marker and add the selected call's historical
            // positions. Suppress duplicate coordinates and points from another floor
            // or coordinate system; history must not replace the group overview.
            if (selectedMarkers.length) markers = [...markers,
                ...selectedMarkers.filter(m => {
                    const selected = visible.find(e => e.marker.callUuid === selectedCall)?.marker;
                    const differs = m.x !== selected?.x || m.y !== selected?.y || m.lat !== selected?.lat || m.lng !== selected?.lng;
                    return differs && (activeContext.startsWith('SBM Ship') ? m.x != null : m.lat != null) &&
                        (activeContext === 'GPS' || m.floor === selected?.floor);
                })
                    .map(m => ({...m, id: `${selectedCall}-${m.id}`, callUuid: selectedCall,
                        timestampLabel: visible.find(e => e.marker.callUuid === selectedCall)?.marker.timestampLabel}))];
        }
        const plan = visible[0]?.plan || {};
        const MapView = plan.isSbmShipLocation ? RelativeFloorPlan : GoogleMapLoader;
        return <>
            {/* Remount on context changes because a Leaflet map keeps its initial CRS. */}
            <MapView key={activeContext} {...plan} allLocationMarkers={markers} showAllLocations={true}
                updateLocations={false} overview={true} onSelect={onSelect} selectedCall={selectedCall}
                locationMarker={visible[0]?.marker}/>
            {/* Match the single-call floor buttons, but switch both markers and plan together. */}
            <nav className="group-floor-controls floor-plan-selector-container" aria-label="Select floor">
                {contexts.map(value => {
                    const [building, floor] = value.split(' — Floor ');
                    const floorLabel = floor === '-1' ? 'B1' : floor === 'unknown' ? 'Unknown floor' : `F${floor}`;
                    const multipleBuildings = new Set(contexts.map(item => item.split(' — Floor ')[0])).size > 1;
                    return <button key={value} type="button" title={value} aria-label={value}
                        aria-pressed={value === activeContext}
                        className={`floor-plan-button${value === activeContext ? ' floor-plan-button-selected' : ''}`}
                        onClick={() => setContext(value)}>
                        {floor === undefined ? building : multipleBuildings ? `${building} · ${floorLabel}` : floorLabel}
                    </button>;
                })}
            </nav>
            <div className="group-map-controls">
                <span className="font-semibold text-slate-800">Reunification View</span>
                <span>{visible.length} of {overview.data?.length || 0} {(overview.data?.length || 0) === 1 ? 'call' : 'calls'}</span>
                {overview.isLoading && <span role="status">Loading group…</span>}
                {overview.isError && <span role="alert">Unable to load group calls.</span>}
                {!overview.isLoading && !overview.isError && !entries.length && <span>No matching calls with coordinates.</span>}
            </div>
        </>;
    };
    // Reuse the current-location/history UI, substituting only its map renderer.
    // Keying by call resets manual history/ALS controls when selection changes.
    if (selectedCall && history.length && !detail.isError) return <Maps key={selectedCall} callUuid={selectedCall}
        history={history} topEmlLocation={history[0]?.payload?.[0]} renderMap={renderMap}/>;
    return <div className="call-details">
        <div className="map-panel">{renderMap()}</div>
        <LocationPanels>
            <section className="location-panel"><h2 className="panel-title">Current Location</h2>
                <p className="panel-message" role={detail.isError ? 'alert' : 'status'}>{detail.isError ? 'Unable to load call details.'
                    : selectedCall ? 'Loading call…' : 'Select a marker or call to view details.'}</p></section>
            <section className="history-panel"><h2 className="panel-title">History</h2></section>
        </LocationPanels>
    </div>;
}
